import { Box, Button, Dialog, DialogContent, Typography } from "@mui/material";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";
import CountrySelect from "components/countrySelector";
import { useContainer, useTemplate } from "containers/templateDetail/store";
import * as React from "react";
import { useTemplateShippingProfiles } from "services/template";
import { useCountries } from "stores/useStore";
import ShippingProfileSelector from "../../productTabs/shipping/shippingProfileSelector";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function SwitchModal(): React.ReactElement {
  const { id: templateID, grams: templateGrams } = useTemplate();
  const [activeCountryCode, setActiveCountryCode] =
    useContainer.activeCountryCode();
  const [open, setOpen] = useContainer.isSwitchModalOpen();
  const { data: countries } = useCountries();

  const defaultCountry = React.useMemo(() => {
    return countries?.find((item) => item.isoNumeric === activeCountryCode);
  }, [activeCountryCode, countries]);

  const { data: shippingProfiles } = useTemplateShippingProfiles({
    id: templateID,
  });

  const [activeShippingProfileID] = useContainer.activeShippingProfileID();

  const activeShippingProfile = React.useMemo(() => {
    return shippingProfiles?.find(
      (item) => item.id === activeShippingProfileID
    );
  }, [activeShippingProfileID]);

  const isBulkShippingNotSupport = React.useMemo(() => {
    if (activeShippingProfile?.type !== "BULK_SHIPPING") {
      return false;
    }
    const gramPrice = activeShippingProfile.gram_prices.find(
      (item) => item.country_code === activeCountryCode
    );

    if (!gramPrice) {
      return true;
    }

    return false;
  }, [activeShippingProfile]);

  const moq = React.useMemo(() => {
    if (activeShippingProfile?.type === "DROP_SHIPPING") {
      return 0;
    }

    if (activeShippingProfile?.type === "BULK_SHIPPING") {
      const gramPrice = activeShippingProfile.gram_prices.find(
        (item) => item.country_id === defaultCountry.id
      );
      if (!gramPrice) {
        return null;
      }
      return Math.ceil(gramPrice.start_gram / templateGrams);
    }
  }, [activeShippingProfile, defaultCountry]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleCountryChange = (ev, country) => {
    setActiveCountryCode(country.isoNumeric);
  };

  return (
    <Dialog
      onClose={handleClose}
      open={open}
      TransitionComponent={Transition}
      maxWidth="xs"
      fullWidth
    >
      <DialogContent>
        <Box sx={{ minWidth: { xs: "100%", sm: 320 } }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Country / Region
          </Typography>
          <CountrySelect
            defaultValue={defaultCountry}
            onChange={handleCountryChange}
          />
          <Typography variant="body1" sx={{ mt: 2, mb: 1 }}>
            Shipping Profile
          </Typography>
          <ShippingProfileSelector />
          {moq > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2">
                <b>Min Order Quantity:</b>&nbsp;&nbsp;{moq}
              </Typography>
            </Box>
          )}
          {isBulkShippingNotSupport && (
            <Box sx={{ mt: 2 }}>
              <Typography color="error" variant="body2">
                *This shipping method is not supported for this country
              </Typography>
            </Box>
          )}
          <Box sx={{ mt: 3, textAlign: "right" }}>
            <Button onClick={handleClose}>Close</Button>
            <Button variant="contained" onClick={handleClose} sx={{ ml: 1 }}>
              OK
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
